import type { WithActiveBranchId } from '@app/api/shared';
import type { ModelRecordProductDiscount } from '@app/types';

export enum ProductsEndpoints {
  UpdateProductCategory = 'UpdateProductCategory',
  GetProductCategoryById = 'GetProductCategoryById',
  GetProductCategories = 'GetProductCategories',
  GetProductBrandById = 'GetProductBrandById',
  GetProductSupplierById = 'GetProductSupplierById',
  SearchProductCategories = 'SearchProductCategories',
  UpdateProductBrand = 'UpdateProductBrand',
  SearchProductBrands = 'SearchProductBrands',
  UpdateProductSupplier = 'UpdateProductSupplier',
  SearchProductSuppliers = 'SearchProductSuppliers',
  UpdateProduct = 'UpdateProduct',
  GetProductFile = 'GetProductFile',
  GetProductLists = 'GetProductLists',
  SearchProductItems = 'SearchProductItems',
  GetProductItemsSearchResult = 'GetProductItemsSearchResult',
  SearchProducts = 'SearchProducts',
  AcceptProducts = 'AcceptProducts',
  DecomissionProducts = 'DecomissionProducts',
  TransferProducts = 'TransferProducts',
  DeleteProducts = 'DeleteProducts',
  GetWarehouseProductsForSelection = 'GetWarehouseProductsForSelection',
  GetWarehouseProductsForSale = 'GetWarehouseProductsForSale',
  GetProductById = 'GetProductById',
  GetProductItemsLists = 'GetProductItemsLists',
  GetProductsRegistryLists = 'GetProductsRegistryLists',
  GetProducts = 'GetProducts',
  GetProductSuppliers = 'GetProductSuppliers',
  SearchProductOperations = 'SearchProductOperations',
  GetProductOperationsSearchResult = 'GetProductOperationsSearchResult',
  GetProductOperationsLists = 'GetProductOperationsLists',
  SearchProductUnits = 'SearchProductUnits',
  GetProductUnitsSearchResult = 'GetProductUnitsSearchResult',
  GetProductUnitsLists = 'GetProductUnitsLists',
  GetProductsLimits = 'GetProductsLimits',
  GetProductsLimitsFilters = 'GetProductsLimitsFilters',
  FindSimilarProducts = 'FindSimilarProducts',
  ImportExcel = 'ImportExcel',
  StopImportExcel = 'StopImportExcel',
}

export interface ModelProductOperationsSearchFilter {
  DateS?: string | null;
  DateE?: string | null;
  SearchTerm?: string | null;
  SearchBy?: 'products' | 'suppliers' | null;
  WarehouseIds?: number[] | null;
  ProductIds?: number[] | null;
  OperationTypeIds?: string[] | null;
  ItemsPerPage: number;
  PageN: number;
}
export interface ModelProductsSearchFilter {
  SearchTerm?: string | null;
  CategoryIds?: number[] | null;
  BrandIds?: number[] | null;
  ItemsPerPage: number;
  PageN: number;
}

export interface ModelProductSearchResult {
  Filter: ModelProductsSearchFilter;
  Products?: ModelProduct[] | null;
  PageN: number;
  CountPages: number;
  CountProducts: number;
}

export interface ModelProductItem {
  ProductId: number;
  Name?: string | null;
  NameShort?: string | null;
  AmountAndUnits?: string | null;
  WarehouseId: number;
  Warehouse?: string | null;
  Count: number;
  PriceOfPurchaseS: number;
  PriceOfPurchaseE: number;
  PriceOfSaleS: number;
  PriceOfSaleE: number;
  UsagesPerMonth: number;
  PurchasesPerMonth: number;
  PurchaseItemsPerMonth: number;
  AmountLeftover: number;
  UnitsOfMeasurement?: string | null;
  IsBelowMinimal: boolean;
  IsBelowCritical: boolean;
  IsDeficit: boolean;
}

export interface ModelProductItemsSearchFilter {
  WarehouseId: number | null;
  SearchTerm?: string | null;
  CategoryIds?: number[] | null;
  BrandIds?: number[] | null;
  PriceOfPurchaseS?: number | null;
  PriceOfPurchaseE?: number | null;
  PriceOfSaleS?: number | null;
  PriceOfSaleE?: number | null;
  IsAtMinimal?: boolean;
  IsAtCritical?: boolean;
  ItemsPerPage: number;
  PageN: number;
}

export interface ModelProductItemsSearchResult {
  Id: number;
  Filter: ModelProductItemsSearchFilter;
  Products?: ModelProductItem[] | null;
  PageN: number;
  CountPages: number;
  CountProducts: number;
}

export interface ModelProductAcceptance {
  ProductId: number;
  Count: number;
  PriceOfPurchase: number;
  PriceOfSale?: number;
}

export interface ModelProductsAcceptance {
  DateAccepted: string;
  WarehouseId: number;
  SupplierId?: number | null;
  Products?: ModelProductAcceptance[] | null;
  Comment?: string | null;
}

export interface ModelProductDecomission {
  ProductId: number;
  SupplierId?: number | null;
  PriceOfSale: number;
  Count: number;
}

export interface ModelProductsDecomission {
  DateDecomission: string;
  WarehouseId: number;
  Products?: ModelProductDecomission[] | null;
  Comment?: string | null;
}

export interface ModelProductsDeleting {
  DateDeletion: string;
  WarehouseId: number;
  Products?: ModelProductDecomission[] | null;
  Comment?: string | null;
}

export interface ModelProductCategory {
  Id: number;
  Name?: string | null;
  Comment?: string | null;
  IsDeleted: boolean;
  ParentCategoryId?: number | null;
}

export interface ModelProductBrand {
  Id: number;
  Name?: string | null;
  Comment?: string | null;
  IsDeleted: boolean;
  CountProducts: number;
}

export interface ModelProductSupplier {
  Id: number;
  Name?: string | null;
  Phone?: string | null;
  Phone1CountryId: number;
  Comment?: string | null;
  IsDeleted: boolean;
}

export interface ProductModelFileDescription {
  Id: number;
  FileName: string | null;
  FileSize: number | null;
  Description: string | null;
  Extension: string | null;
  Thumbnail: string | null;
}

export interface ProductModelFile {
  Id: number;
  Name?: string | null;
  Data?: string | null;
  Lifetime?: string | null;
  Description?: string | null;
}

export interface ProductModelItem {
  Id: number;
  Name: string;
  IdS: string;
}

export interface ModelProduct {
  Id: number;
  Name?: string | null;
  NameShort?: string | null;
  Article?: string | null;
  BarCode?: string | null;
  Comment?: string | null;
  UnitOfMeasurement?: string | null;
  UnitOfMeasurementId: number;
  Amount?: number | null;
  ProductCategoryId?: number | null;
  ProductBrandId?: number | null;
  IsDeleted?: boolean;
  ReadFiles?: ProductModelFileDescription[] | null;
  EditFiles?: ProductModelFile[] | null;
  SupplierId?: number;
  ProductCategory?: string | null;
}

export interface ModelProductLists {
  UnitsOfMeasurements: ProductModelItem[] | null;
  ProductBrands: ProductModelItem[] | null;
  ProductCategories: ProductModelItem[] | null;
}

export interface ModelProductCategoriesFilter {
  SearchTerm?: string | null;
  ItemsPerPage: number;
  Page: number;
}

export interface ModelProductCategoriesSearchResult {
  Filter: ModelProductCategoriesFilter;
  Items?: ModelProductCategory[] | null;
  ItemsPerPage: number;
  Page: number;
  CountItems: number;
}

export interface ModelProductBrandsFilter {
  SearchTerm?: string | null;
  ItemsPerPage: number;
  Page: number;
}
export interface ModelProductBrandsSearchResult {
  Filter: ModelProductBrandsFilter;
  Items?: ModelProductBrand[] | null;
  ItemsPerPage: number;
  Page: number;
  CountItems: number;
}

export interface ModelProductSuppliersFilter {
  SearchTerm?: string | null;
  ItemsPerPage: number;
  Page: number;
}

export interface ModelProductSuppliersSearchResult {
  Filter: ModelProductSuppliersFilter;
  Items?: ModelProductSupplier[] | null;
  ItemsPerPage: number;
  Page: number;
  CountItems: number;
}
export interface GetProductItemsSearchResultParams {
  searchId: number;
  pageN: number;
  itemsPerPage: number;
}

export interface SearchProductListItem {
  Id: number;
  IdS: string;
  Name: string;
}

export type SearchProductList = SearchProductListItem[];

export interface SearchProductLists {
  Warehouses: SearchProductList;
  Categories: SearchProductList;
  Brands: SearchProductList;
}
export interface GetProductsParams {
  productId?: number | null;
  includeIds?: number[];
}

interface GetProductSuppliersParams {
  supplierId?: number | null;
}

export interface ModelProductOperation {
  Timestamp: string;
  DocumentId: number;
  SupplierId?: number | null;
  Supplier?: string | null;
  Operation?: string | null;
  WarehouseId: number;
  Warehouse?: string | null;
  ProductId: number;
  Product?: string | null;
  CountProducts: number;
  Usage: number | null;
  PriceOfPurchase: number;
  CountLeftAtWarehouse: number;
  UserId: number;
  UserName?: string | null;
}

export interface ModelProductOperationsSearchResult {
  Id: number;
  Filter: ModelProductOperationsSearchFilter;
  Operations?: ModelProductOperation[] | null;
  PageN: number;
  CountPages: number;
  CountOperations: number;
}

export interface GetProductOperationsSearchResultParams {
  searchId: number;
  pageN: number;
  itemsPerPage: number;
}

export interface ModelProductOperationsLists {
  Warehouses: ProductModelItem[] | null;
  OperationTypes: ProductModelItem[] | null;
  Products: ProductModelItem[] | null;
}

export interface ModelSelectProductsFilter {
  WarehouseId: number;
  SearchTerm?: string | null;
  ProductIds?: number[] | null;
  SupplierIds?: number[] | null;
  PriceOfSaleS?: number | null;
  PriceOfSaleE?: number | null;
}

export interface ModelSelectProducts {
  ProductId: number;
  Product?: string | null;
  ProductCategoryId?: number | null;
  ProductCategory?: string | null;
  BrandId?: number | null;
  Brand?: string | null;
  SupplierId?: number | null;
  Supplier?: string | null;
  PriceOfSale: number;
  CountAvailable: number;
}

export interface ModelProductForSale {
  WarehouseId: number;
  ProductId: number;
  ProductName?: string | null;
  PriceOfSale: number;
  CountAvailable: number;
  // Backend swagger spells this field correctly, but the typo variant is
  // documented as a possible legacy field. Keep it optional so we can
  // normalize it on the client without losing type-safety.
  CountAvaialbe?: number;
}

export interface ModelRecordProduct {
  WarehouseId: number;
  ProductId: number;
  GroupId: string | null;
  Name?: string | null;
  Price: number;
  Discount: number;
  Paid: number;
  Count: number;
  SellerId?: number | null;
  Discounts: ModelRecordProductDiscount[] | null;
  TotalDiscount: number;
  Cost: number;
  IsExcludedFromLoyaltyDiscount: boolean;
}

export interface ModelProductTransfer {
  ProductId: number;
  SupplierId?: number | null;
  PriceOfSale: number;
  Count: number;
}
export interface ModelProductsTransfer {
  DateTransfer: string;
  WarehouseFromId: number;
  WarehouseToId: number;
  Products?: ModelProductTransfer[] | null;
  Comment?: string | null;
}

export interface ModelProductUnit {
  Id: number;
  DateAccepted: string;
  ProductId: number;
  Product?: string | null;
  PriceOfPurchase: number;
  PriceOfSale: number;
  WarehouseId: number;
  Warehouse?: string | null;
  SupplierId: number;
  Supplier?: string | null;
  Credit: number;
  StateId?: string | null;
  State?: string | null;
  CountDocuments: number;
}
export interface ModelProductUnitsSearchFilter {
  DateS?: string | null;
  DateE?: string | null;
  WarehouseIds?: number[] | null;
  ProductIds?: number[] | null;
  StateIds?: number[] | string[] | null;
  ItemsPerPage: number;
  PageN: number;
}

export interface ModelProductUnitsSearchResult {
  Id: number;
  Filter: ModelProductUnitsSearchFilter;
  ProductUnits?: ModelProductUnit[] | null;
  PageN: number;
  CountPages: number;
  CountProductUnits: number;
}

export interface GetProductUnitsSearchResultParams {
  searchId: number;
  pageN: number;
  itemsPerPage: number;
}

export interface ModelProductUnitsLists {
  Warehouses: ProductModelItem[] | null;
  Products: ProductModelItem[] | null;
  States: ProductModelItem[] | null;
}

export interface ModelProductLimit {
  Id: number;
  ProductId: number;
  Name?: string | null;
  Category?: string | null;
  NameShort?: string | null;
  Article?: string | null;
  BarCode?: string | null;
  DesiredLeftover?: number | null;
  CriticalLeftover?: number | null;
  AcceptanceOfReject?: number | null;
}
export interface GetProductsLimitsRequest extends WithActiveBranchId {
  warehouseId: number;
}

export type GetProductUnitsSearchResultRequest =
  WithActiveBranchId<GetProductUnitsSearchResultParams>;

export type SearchProductUnitsRequest = WithActiveBranchId<{
  data: ModelProductUnitsSearchFilter;
}>;

export type ModelProductsDecomissionRequest = WithActiveBranchId<{
  data: ModelProductsDecomission;
}>;

export type ModelProductsDeletingRequest = WithActiveBranchId<{
  data: ModelProductsDeleting;
}>;

export type ModelSelectProductsRequest = WithActiveBranchId<{
  data: ModelSelectProductsFilter;
}>;

export type GetWarehouseProductsForSaleRequest = WithActiveBranchId<{
  warehouseId: number;
}>;

export type GetWarehouseProductsForSaleResponse = ModelProductForSale[];

export type ModelProductsSearchFilterRequest =
  WithActiveBranchId<ModelProductsSearchFilter>;

export type GetProductFileResponse = ProductModelFile;

export type GetProductListsResponse = ModelProductLists;

export type GetProductItemsSearchResultRequest =
  WithActiveBranchId<GetProductItemsSearchResultParams>;

export type GetProductOperationsSearchResultRequest =
  WithActiveBranchId<GetProductOperationsSearchResultParams>;

export type UpdateProductCategoryRequest = WithActiveBranchId<{
  data: ModelProductCategory;
}>;

export type UpdateProductBrandRequest = WithActiveBranchId<{
  data: ModelProductBrand;
}>;

export type UpdateProductSupplierRequest = WithActiveBranchId<{
  data: ModelProductSupplier;
}>;

export type UpdateProductRequest = WithActiveBranchId<{
  data: ModelProduct;
}>;

export type SearchProductCategoriesRequest = WithActiveBranchId<{
  data: ModelProductCategoriesFilter;
}>;

export type SearchProductBrandsRequest = WithActiveBranchId<{
  data: ModelProductBrandsFilter;
}>;

export type SearchProductSuppliersRequest = WithActiveBranchId<{
  data: ModelProductSuppliersFilter;
}>;

export type GetProductCategoryByIdRequest = WithActiveBranchId<{
  categoryId: number;
}>;

export type GetProductBrandByIdRequest = WithActiveBranchId<{
  brandId: number;
}>;

export type GetProductSupplierByIdRequest = WithActiveBranchId<{
  supplierId: number;
}>;

export type GetProductFileRequest = WithActiveBranchId<{
  fileId: number;
}>;

export type GetProductListsRequest = WithActiveBranchId<{
  brandId: number | null;
  categoryId: number | null;
}>;

export type GetProductByIdRequest = WithActiveBranchId<{
  productId: number | null;
}>;

export type SearchProductItemsRequest = WithActiveBranchId<{
  data: ModelProductItemsSearchFilter;
}>;

export type GetProductSuppliersRequest =
  WithActiveBranchId<GetProductSuppliersParams>;

export type GetProductsRequest = WithActiveBranchId<GetProductsParams>;

export type SearchProductOperationsRequest = WithActiveBranchId<{
  data: ModelProductOperationsSearchFilter;
}>;

export type AcceptProductsRequest = WithActiveBranchId<{
  data: ModelProductsAcceptance;
}>;

export type TransferProductsRequest = WithActiveBranchId<{
  data: ModelProductsTransfer;
}>;

export interface ModelProductsLimitsFilterData {
  Warehouses: ProductModelItem[];
  ProductCategories: ProductModelItem[];
}

export interface FindSimilarProductsParams {
  Name?: string | null;
  NameShort?: string | null;
  Article?: string | null;
  BarCode?: string | null;
  UnitOfMeasurementId?: number | null;
  UnitOfMeasurement?: string | null;
  ProductCategoryId?: number | null;
  ProductBrandId?: number | null;
}

export type ImportExcelRequest = WithActiveBranchId<{
  extension: string;
  data: string;
  warehouseId: number;
}>;

export type StopImportExcelRequest = WithActiveBranchId;

export type ImportExcelResponse = void;
export type StopImportExcelResponse = void;
